import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Footer from './Footer';
import './AthenaPage.css';

const AthenaPage = () => {
  const navigate = useNavigate();
  const { t, tArray } = useLanguage();
  const [isMuted, setIsMuted] = React.useState(true);

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const features = tArray('athena.features');

  const useCases = tArray('athena.useCases');

  return (
    <>
      <Helmet>
        <title>ΑΘΗΝΑ - Πολυγλωσσικό Ρομποτικό Σύστημα Υποδοχής | AI Agency</title>
        <meta name="description" content="ΑΘΗΝΑ: Καινοτόμο ρομποτικό σύστημα με AI για δημόσιες υπηρεσίες. Υποστηρίζει 50+ γλώσσες, παρέχει πληροφορίες για άσυλο 24/7 και απελευθερώνει το προσωπικό." />
        <meta name="keywords" content="ΑΘΗΝΑ, ρομποτικό σύστημα, AI υποδοχή, πολυγλωσσικό, άσυλο, δημόσιες υπηρεσίες, τεχνητή νοημοσύνη, multilingual robot, asylum services" />
        <link rel="canonical" href="https://www.aiagency-greece.com/athena" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="el" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content="ΑΘΗΝΑ - Πολυγλωσσικό Ρομποτικό Σύστημα Υποδοχής" />
        <meta property="og:description" content="Καινοτόμο ρομποτικό σύστημα με AI για δημόσιες υπηρεσίες. Υποστηρίζει 50+ γλώσσες και λειτουργεί 24/7." />
        <meta property="og:url" content="https://www.aiagency-greece.com/athena" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_geekaisite/artifacts/t7a1295q_Sofia%201.png" />
        <meta property="og:locale" content="el_GR" />
        <meta property="og:site_name" content="AI Agency Greece" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ΑΘΗΝΑ - Πολυγλωσσικό Ρομποτικό Σύστημα" />
        <meta name="twitter:description" content="Καινοτόμο ρομποτικό σύστημα με AI για δημόσιες υπηρεσίες που υποστηρίζει 50+ γλώσσες." />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_geekaisite/artifacts/t7a1295q_Sofia%201.png" />
        
        {/* Service Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "AI-Powered Reception System",
            "name": "ΑΘΗΝΑ - Πολυγλωσσικό Ρομποτικό Σύστημα",
            "description": "Ρομποτικό σύστημα υποδοχής με τεχνητή νοημοσύνη για δημόσιες υπηρεσίες που εξυπηρετεί αιτούντες άσυλο σε 50+ γλώσσες.",
            "provider": {
              "@type": "Organization",
              "name": "PROTOS AI AGENCY",
              "url": "https://ai-agency.gr/"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Greece"
            },
            "category": "Public Service Technology"
          })}
        </script>
        
        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Αρχική",
                "item": "https://www.aiagency-greece.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "ΑΘΗΝΑ",
                "item": "https://www.aiagency-greece.com/athena"
              }
            ]
          })}
        </script>
        
        {/* VideoObject Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "ΑΘΗΝΑ Robot Demo Video",
            "description": "Demonstration του πολυγλωσσικού ρομποτικού συστήματος ΑΘΗΝΑ σε λειτουργία.",
            "thumbnailUrl": "https://customer-assets.emergentagent.com/job_geekaisite/artifacts/t7a1295q_Sofia%201.png",
            "uploadDate": "2025-01-30",
            "contentUrl": "https://customer-assets.emergentagent.com/job_geekaisite/artifacts/xn0l2k5s_Athena%20Video.mp4"
          })}
        </script>
      </Helmet>

      <div className="athena-page">
        {/* Back Button */}
        <div className="athena-back-button-container">
          <button className="athena-back-button" onClick={() => navigate('/')}>
            ← {t('athena.backButton')}
          </button>
        </div>

        {/* Hero Section */}
        <section className="athena-hero">
          <div className="athena-hero-content">
            <div className="athena-logo">{t('athena.logo')}</div>
            <h1 className="athena-title">{t('athena.title')}</h1>
            <p className="athena-subtitle">
              {t('athena.subtitle')}
            </p>
            <p className="athena-tagline-large">{t('athena.tagline')}</p>
          </div>
        </section>

        {/* Robot Images */}
        <section className="athena-showcase">
          <div className="athena-container">
            <h2 className="athena-section-title">{t('athena.showcaseTitle')}</h2>
            <p className="athena-section-subtitle">{t('athena.showcaseSubtitle')}</p>
            
            <div className="athena-image-gallery">
              <div className="athena-gallery-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_geekaisite/artifacts/t7a1295q_Sofia%201.png" 
                  alt="ΑΘΗΝΑ Robot - Frontansicht mit Display"
                  loading="lazy"
                  className="athena-gallery-image"
                />
              </div>
              <div className="athena-gallery-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_geekaisite/artifacts/ulqg7c6h_Sofia2.png" 
                  alt="ΑΘΗΝΑ Robot - Seitenansicht"
                  loading="lazy"
                  className="athena-gallery-image"
                />
              </div>
              <div className="athena-gallery-item">
                <img 
                  src="https://customer-assets.emergentagent.com/job_geekaisite/artifacts/g5dcuwjs_Sofia%203.png" 
                  alt="ΑΘΗΝΑ Robot - Interaktion im Einsatz"
                  loading="lazy"
                  className="athena-gallery-image"
                />
              </div>
            </div>

            {/* Video */}
            <div className="athena-video-container">
              <video 
                className="athena-video"
                autoPlay 
                loop 
                muted={isMuted}
                playsInline
                preload="metadata"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_geekaisite/artifacts/xn0l2k5s_Athena%20Video.mp4" 
                  type="video/mp4" 
                />
                Ihr Browser unterstützt das Video-Tag nicht.
              </video>
              
              {/* Mute/Unmute Button */}
              <button 
                className="athena-audio-toggle"
                onClick={toggleMute}
                aria-label={isMuted ? "Ton einschalten" : "Ton ausschalten"}
              >
                {isMuted ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <line x1="23" y1="9" x2="17" y2="15"></line>
                    <line x1="17" y1="9" x2="23" y2="15"></line>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="athena-features">
          <div className="athena-container">
            <h2 className="athena-section-title">{t('athena.featuresTitle')}</h2>
            <p className="athena-section-subtitle">
              {t('athena.featuresSubtitle')}
            </p>
            <div className="athena-features-grid">
              {features.map((feature, index) => (
                <div key={index} className="athena-feature-card">
                  <div className="athena-feature-icon">{feature.icon}</div>
                  <h3 className="athena-feature-title">{feature.title}</h3>
                  <p className="athena-feature-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="athena-benefits">
          <div className="athena-container">
            <h2 className="athena-section-title">{t('athena.benefitsTitle')}</h2>
            <div className="athena-benefits-grid">
              <div className="athena-benefit-row">
                <div className="athena-benefit-box">
                  <h3 className="athena-benefit-title">{t('athena.benefits.costReduction.title')}</h3>
                  <ul className="athena-benefit-list">
                    {tArray('athena.benefits.costReduction.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="athena-benefit-box">
                  <h3 className="athena-benefit-title">{t('athena.benefits.efficiency.title')}</h3>
                  <ul className="athena-benefit-list">
                    {tArray('athena.benefits.efficiency.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="athena-benefit-row">
                <div className="athena-benefit-box">
                  <h3 className="athena-benefit-title">{t('athena.benefits.service.title')}</h3>
                  <ul className="athena-benefit-list">
                    {tArray('athena.benefits.service.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className="athena-benefit-box">
                  <h3 className="athena-benefit-title">{t('athena.benefits.staff.title')}</h3>
                  <ul className="athena-benefit-list">
                    {tArray('athena.benefits.staff.items').map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                    <li>Βελτίωση ικανοποίησης εργαζομένων</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="athena-stats">
          <div className="athena-container">
            <h2 className="athena-section-title">Τεχνικά Χαρακτηριστικά</h2>
            <div className="athena-stats-grid">
              <div className="athena-stat-card">
                <div className="athena-stat-number">50+</div>
                <div className="athena-stat-label">Υποστηριζόμενες Γλώσσες</div>
              </div>
              <div className="athena-stat-card">
                <div className="athena-stat-number">24/7</div>
                <div className="athena-stat-label">Διαθεσιμότητα</div>
              </div>
              <div className="athena-stat-card">
                <div className="athena-stat-number">9</div>
                <div className="athena-stat-label">Κύριες Λειτουργίες</div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Specifications Section */}
        <section className="athena-tech-specs" style={{padding: '4rem 0', background: 'rgba(0, 0, 0, 0.3)'}}>
          <div className="athena-container">
            <h2 className="athena-section-title">Τεχνικές Προδιαγραφές</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" style={{maxWidth: '1200px', margin: '0 auto'}}>
              <div style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(255, 255, 255, 0.1)'}}>
                <h3 style={{color: '#60a5fa', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem'}}>● AI & Επεξεργασία Γλώσσας</h3>
                <ul style={{color: '#d1d5db', lineHeight: '1.8'}}>
                  <li>• Προηγμένα NLP μοντέλα για 50+ γλώσσες</li>
                  <li>• Αναγνώριση φωνής σε πραγματικό χρόνο</li>
                  <li>• Σύνθεση φυσικής ομιλίας</li>
                  <li>• Ανάλυση συναισθήματος και προθέσεων</li>
                </ul>
              </div>
              <div style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(255, 255, 255, 0.1)'}}>
                <h3 style={{color: '#60a5fa', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem'}}>● Hardware & Σχεδιασμός</h3>
                <ul style={{color: '#d1d5db', lineHeight: '1.8'}}>
                  <li>• Οθόνη αφής 21.5" HD</li>
                  <li>• Ενσωματωμένη κάμερα και μικρόφωνο</li>
                  <li>• Ανθεκτικός σχεδιασμός για δημόσιους χώρους</li>
                  <li>• Χαμηλή ενεργειακή κατανάλωση</li>
                </ul>
              </div>
              <div style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(255, 255, 255, 0.1)'}}>
                <h3 style={{color: '#60a5fa', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem'}}>● Ασφάλεια & Συμμόρφωση</h3>
                <ul style={{color: '#d1d5db', lineHeight: '1.8'}}>
                  <li>• Πλήρης GDPR συμμόρφωση</li>
                  <li>• ISO 27001 πιστοποίηση ασφάλειας</li>
                  <li>• Κρυπτογράφηση δεδομένων end-to-end</li>
                  <li>• On-premise εγκατάσταση δυνατή</li>
                </ul>
              </div>
              <div style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(255, 255, 255, 0.1)'}}>
                <h3 style={{color: '#60a5fa', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem'}}>● Ενσωμάτωση & Υποστήριξη</h3>
                <ul style={{color: '#d1d5db', lineHeight: '1.8'}}>
                  <li>• API για ενσωμάτωση με υπάρχοντα συστήματα</li>
                  <li>• Εκπαίδευση προσωπικού περιλαμβάνεται</li>
                  <li>• 24/7 τεχνική υποστήριξη</li>
                  <li>• Τακτικές ενημερώσεις λογισμικού</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="athena-use-cases" style={{padding: '4rem 0'}}>
          <div className="athena-container">
            <h2 className="athena-section-title">Πεδία Εφαρμογής</h2>
            <p className="athena-section-subtitle">Το ΑΘΗΝΑ μπορεί να προσαρμοστεί σε διάφορους δημόσιους φορείς</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{maxWidth: '1200px', margin: '2rem auto 0'}}>
              <div style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(59, 130, 246, 0.3)', textAlign: 'center'}}>
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>🏛️</div>
                <h3 style={{color: '#fff', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Υπηρεσίες Ασύλου</h3>
                <p style={{color: '#9ca3af'}}>Υποδοχή και πληροφόρηση αιτούντων άσυλο σε πολλές γλώσσες</p>
              </div>
              <div style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(59, 130, 246, 0.3)', textAlign: 'center'}}>
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>🏥</div>
                <h3 style={{color: '#fff', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Δημόσια Νοσοκομεία</h3>
                <p style={{color: '#9ca3af'}}>Υποδοχή και καθοδήγηση ασθενών από διάφορες χώρες</p>
              </div>
              <div style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(59, 130, 246, 0.3)', textAlign: 'center'}}>
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>🏢</div>
                <h3 style={{color: '#fff', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Δημόσιες Υπηρεσίες</h3>
                <p style={{color: '#9ca3af'}}>Γενική πληροφόρηση και εξυπηρέτηση πολιτών</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="athena-cta">
          <div className="athena-container">
            <h2 className="athena-cta-title">Έτοιμοι να Μετασχηματίσετε τις Υπηρεσίες σας;</h2>
            <p className="athena-cta-text">
              Ανακαλύψτε πώς το ΑΘΗΝΑ μπορεί να βελτιώσει την εξυπηρέτηση και να εξοικονομήσει πόρους.
            </p>
            <button className="athena-btn athena-btn-primary" onClick={() => {
              navigate('/');
              setTimeout(() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }, 100);
            }}>
              Επικοινωνήστε Μαζί Μας
            </button>
          </div>
        </section>

        {/* Related Services Section */}
        <section style={{padding: '4rem 0', background: 'rgba(0, 0, 0, 0.2)'}}>
          <div className="athena-container">
            <h2 className="athena-section-title">Εξερευνήστε τις Υπηρεσίες μας</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{maxWidth: '1200px', margin: '2rem auto 0'}}>
              <a href="/services/dimosiadioikisi" style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(96, 165, 250, 0.3)', textDecoration: 'none', transition: 'all 0.3s'}} className="hover:border-blue-400">
                <h3 style={{color: '#60a5fa', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Δημόσια Διοίκηση</h3>
                <p style={{color: '#9ca3af'}}>Ψηφιακός μετασχηματισμός δημόσιων υπηρεσιών με AI</p>
              </a>
              <a href="/services/asfaleia" style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(239, 68, 68, 0.3)', textDecoration: 'none', transition: 'all 0.3s'}} className="hover:border-red-400">
                <h3 style={{color: '#ef4444', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Ασφάλεια & Άμυνα</h3>
                <p style={{color: '#9ca3af'}}>Προηγμένα συστήματα επιτήρησης και προστασίας</p>
              </a>
              <a href="/services/politistiki-klironomia" style={{background: 'rgba(255, 255, 255, 0.05)', padding: '2rem', borderRadius: '1rem', border: '1px solid rgba(168, 85, 247, 0.3)', textDecoration: 'none', transition: 'all 0.3s'}} className="hover:border-purple-400">
                <h3 style={{color: '#a855f7', fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem'}}>Πολιτιστική Κληρονομιά</h3>
                <p style={{color: '#9ca3af'}}>Ψηφιοποίηση χειρογράφων και ιστορικών αρχείων</p>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default AthenaPage;
